import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';
import { WEB_API_BASE_URL } from '../config/config';

const FaSync = Icons.FaSync as any;
const FaCheckCircle = Icons.FaCheckCircle as any;

interface AgreementTemplateProps {
  fieldId: string;
  value: boolean;
  onChange: (value: boolean) => void;
  formData: Record<string, any>;
  disabled?: boolean;
  hasError?: boolean;
}

interface TemplateMapping {
  placeholder: string;
  sourceFieldId: string;
  fallbackValue: string;
  transform?: string | null;
}

interface TemplateConfig {
  template: string;
  mappings: TemplateMapping[];
}

const Container = styled.div`
  width: 100%;
`;

const AgreementCard = styled.div<{ hasError?: boolean }>`
  border: 2px solid ${props => props.hasError ? props.theme.error : props.theme.border};
  border-radius: 8px;
  background: white;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, ${props => props.theme.primary}05 0%, ${props => props.theme.primary}10 100%);
  border-bottom: 2px solid ${props => props.theme.primary};
`;

const Title = styled.span`
  font-weight: 600;
  color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
`;

const RefreshButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.primary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primary}15;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: ${props => props.disabled ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Content = styled.div`
  padding: 1.5rem;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.6;
  color: ${props => props.theme.textPrimary};
  white-space: pre-wrap;
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.primary};
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
  
  strong {
    font-weight: 600;
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme.error};
`;

const AcknowledgmentSection = styled.div`
  padding: 1.25rem;
  border-top: 2px solid ${props => props.theme.border};
  background: linear-gradient(to bottom, ${props => props.theme.surface}, #fafafa);
`;

const CheckboxWrapper = styled.label<{ checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.2s;
  background: ${props => props.checked ? props.theme.success + '10' : 'transparent'};
  border: 2px solid ${props => props.checked ? props.theme.success : props.theme.border};
  
  &:hover {
    background: ${props => props.checked ? props.theme.success + '15' : props.theme.surface};
  }
  
  input {
    width: 22px;
    height: 22px;
    cursor: pointer;
    accent-color: ${props => props.theme.success};
  }
  
  span {
    font-weight: 500;
    color: ${props => props.checked ? props.theme.success : props.theme.textPrimary};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const AgreementTemplate: React.FC<AgreementTemplateProps> = ({
  fieldId,
  value,
  onChange,
  formData,
  disabled = false,
  hasError = false
}) => {
  const [templateContent, setTemplateContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadTemplate();
  }, [fieldId]);

  useEffect(() => {
    // Refresh template when form data changes
    if (templateContent && !isLoading) {
      processTemplate();
    }
  }, [formData]);

  const loadTemplate = async () => {
    setIsLoading(true);
    setError('');

    try {
      const authToken = localStorage.getItem('authToken');
      const apiUrl = `${WEB_API_BASE_URL}/AgreementTemplate/GetTemplate?templateName=${encodeURIComponent(fieldId)}`;

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.status}`);
      }

      const templateConfig: TemplateConfig = await response.json();
      processTemplateConfig(templateConfig);
    } catch (err: any) {
      console.error('Error loading agreement template:', err);
      setError('Unable to load agreement template. Please try again.');
      setTemplateContent('Agreement template is currently unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const processTemplateConfig = (config: TemplateConfig) => {
    let processedContent = config.template || '';

    // Replace placeholders with form data
    config.mappings?.forEach((mapping: TemplateMapping) => {
      const sourceValue = getFieldValue(mapping.sourceFieldId) || mapping.fallbackValue || '____________';
      const placeholder = mapping.placeholder;
      
      // Replace all occurrences of the placeholder
      processedContent = processedContent.split(placeholder).join(sourceValue);
    });

    setTemplateContent(processedContent);
  };

  const processTemplate = () => {
    // Re-process template with current form data
    // This is called when form data changes
    loadTemplate();
  };

  const getFieldValue = (fieldId: string): string => {
    const value = formData[fieldId];
    if (value === undefined || value === null) return '';
    
    // Handle date formatting
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    
    // Handle arrays (multiselect)
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return String(value);
  };

  const handleRefresh = () => {
    loadTemplate();
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <Container>
      <AgreementCard hasError={hasError}>
        <Header>
          <Title>
            📄 Agreement Template
          </Title>
          <RefreshButton
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh template data"
          >
            <FaSync />
            Refresh
          </RefreshButton>
        </Header>

        <Content>
          {isLoading && <LoadingMessage>Loading agreement template...</LoadingMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {!isLoading && !error && (
            <div dangerouslySetInnerHTML={{ __html: templateContent }} />
          )}
        </Content>

        <AcknowledgmentSection>
          <CheckboxWrapper checked={value}>
            <input
              type="checkbox"
              checked={value}
              onChange={handleCheckboxChange}
              disabled={disabled}
            />
            <span>
              {React.createElement(FaCheckCircle as any, { 
                style: { color: value ? '#4CAF50' : '#ccc', fontSize: '1.25rem' } 
              })}
              I acknowledge and agree to the terms above
            </span>
          </CheckboxWrapper>
        </AcknowledgmentSection>
      </AgreementCard>
    </Container>
  );
};

export default AgreementTemplate;

